import React, { useEffect, useState, useCallback } from "react";
import {
  Target, Plus, Sparkles, Trash2, Check, Pause, Play, CheckCircle2, Archive, Wand2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const CATEGORIES = ["saving", "learning", "business", "travel", "fitness", "project", "custom"];
const STATUS_TONE = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  paused: "border-amber-200 bg-amber-50 text-amber-700",
  completed: "border-sky-200 bg-sky-50 text-sky-700",
  archived: "border-slate-200 bg-slate-50 text-slate-500",
};

function ProgressBar({ value, testid }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" data-testid={testid}>
      <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${value || 0}%` }} />
    </div>
  );
}

export default function Goals() {
  const { t, language } = useLanguage();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/goals").then(({ data }) => setGoals(data)).catch(() => setGoals([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = goals.find((g) => g.goal_id === openId) || null;
  const patchGoal = (g) => setGoals((prev) => prev.map((x) => (x.goal_id === g.goal_id ? g : x)));

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl" data-testid="goals-title">{t("goals_title")}</h1>
            <p className="mt-1.5 text-slate-500">{t("goals_subtitle")}</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="goal-new-btn" className="gap-1.5 rounded-full min-h-[44px]">
            <Plus className="h-4 w-4" /> {t("goal_new")}
          </Button>
        </div>

        <div className="mt-8 space-y-3" data-testid="goals-list">
          {loading ? (
            [0, 1].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />)
          ) : goals.length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center" data-testid="goals-empty">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><Target className="h-7 w-7" /></span>
              <p className="mt-4 max-w-sm text-sm text-slate-500">{t("goal_empty")}</p>
              <Button onClick={() => setShowCreate(true)} data-testid="goal-empty-cta" className="mt-5 gap-1.5 rounded-full min-h-[44px]">
                <Plus className="h-4 w-4" /> {t("goal_new")}
              </Button>
            </div>
          ) : (
            goals.map((g) => (
              <button key={g.goal_id} onClick={() => setOpenId(g.goal_id)} data-testid={`goal-card-${g.goal_id}`}
                className="block w-full rounded-2xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-primary/40">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate font-display font-semibold text-slate-900">{g.title}</h3>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="rounded-full text-xs">{t(`cat_${g.category}`)}</Badge>
                      <Badge variant="outline" className={`rounded-full text-xs ${STATUS_TONE[g.status] || ""}`} data-testid={`goal-status-${g.goal_id}`}>{t(`status_${g.status}`)}</Badge>
                      {g.target_date && <span className="text-xs text-slate-400">🎯 {g.target_date}</span>}
                    </div>
                  </div>
                  <span className="flex-shrink-0 text-sm font-bold text-primary" data-testid={`goal-progress-val-${g.goal_id}`}>{g.progress}%</span>
                </div>
                <div className="mt-3"><ProgressBar value={g.progress} testid={`goal-progress-${g.goal_id}`} /></div>
              </button>
            ))
          )}
        </div>
      </main>
      <Footer />

      <CreateGoalDialog open={showCreate} onOpenChange={setShowCreate} language={language}
        onCreated={(g) => { setGoals((prev) => [g, ...prev]); setShowCreate(false); setOpenId(g.goal_id); }} />

      <GoalDetailDialog goal={open} onClose={() => setOpenId(null)} onChange={patchGoal}
        onDeleted={(id) => { setGoals((prev) => prev.filter((x) => x.goal_id !== id)); setOpenId(null); }} language={language} />
    </div>
  );
}

function CreateGoalDialog({ open, onOpenChange, onCreated }) {
  const { t } = useLanguage();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("custom");
  const [targetDate, setTargetDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (title.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post("/goals", {
        title: title.trim(), description: description.trim(), category,
        target_date: targetDate || null, priority,
      });
      toast.success(t("goal_saved"));
      setTitle(""); setDescription(""); setCategory("custom"); setTargetDate(""); setPriority("medium");
      onCreated(data);
    } catch (e) { notifyError(e, t); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="goal-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Target className="h-5 w-5 text-primary" /> {t("goal_new")}</DialogTitle>
          <DialogDescription>{t("goals_subtitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t("goal_title_label")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("goal_title_ph")} data-testid="goal-title-input" className="mt-1.5 min-h-[48px]" maxLength={140} />
          </div>
          <div>
            <Label>{t("goal_desc_label")}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("goal_desc_ph")} data-testid="goal-desc-input" className="mt-1.5" maxLength={1000} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("goal_category")}</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="goal-category-select"
                className="mt-1.5 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
              </select>
            </div>
            <div>
              <Label>{t("goal_priority")}</Label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="goal-priority-select"
                className="mt-1.5 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
          </div>
          <div>
            <Label>{t("goal_target_date")}</Label>
            <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} data-testid="goal-target-input" className="mt-1.5 min-h-[48px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("memory_cancel")}</Button>
          <Button onClick={submit} disabled={busy || title.trim().length < 2} data-testid="goal-create-submit" className="gap-1.5 rounded-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("goal_create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GoalDetailDialog({ goal, onClose, onChange, onDeleted, language }) {
  const { t } = useLanguage();
  const [planning, setPlanning] = useState(false);
  const [smart, setSmart] = useState("");
  const [smartBusy, setSmartBusy] = useState(false);

  if (!goal) return null;
  const ms = goal.milestones || [];

  const generatePlan = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post(`/goals/${goal.goal_id}/plan`, { language });
      onChange(data);
      toast.success(t("goal_plan_ready"));
    } catch (e) { notifyError(e, t); } finally { setPlanning(false); }
  };

  const toggleStep = async (milestone_id, step_id, done) => {
    try {
      const { data } = await api.put(`/goals/${goal.goal_id}/progress`, { milestone_id, step_id, done });
      onChange(data);
    } catch (e) { notifyError(e, t); }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.put(`/goals/${goal.goal_id}`, { status });
      onChange(data);
      toast.success(t("goal_saved"));
    } catch (e) { notifyError(e, t); }
  };

  const applySmart = async () => {
    if (smart.trim().length < 2) return;
    setSmartBusy(true);
    try {
      const { data } = await api.post(`/goals/${goal.goal_id}/smart-update`, { message: smart.trim() });
      onChange(data.goal);
      setSmart("");
      toast.success((data.changes || []).join(", ") || t("goal_saved"));
    } catch (e) { notifyError(e, t); } finally { setSmartBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("goal_delete_confirm"))) return;
    try {
      await api.delete(`/goals/${goal.goal_id}`);
      toast.success(t("goal_deleted"));
      onDeleted(goal.goal_id);
    } catch (e) { notifyError(e, t); }
  };

  return (
    <Dialog open={!!goal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="goal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Target className="h-5 w-5 text-primary" /> {goal.title}</DialogTitle>
          <DialogDescription>
            {t(`cat_${goal.category}`)} · {t(`status_${goal.status}`)}{goal.target_date ? ` · 🎯 ${goal.target_date}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <ProgressBar value={goal.progress} testid="goal-detail-progress" />
          <span className="text-sm font-bold text-primary">{goal.progress}%</span>
        </div>

        {goal.objective && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("goal_objective")}</p>
            <p className="mt-0.5 text-sm text-slate-700" data-testid="goal-objective">{goal.objective}</p>
          </div>
        )}

        {ms.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-center">
            <p className="text-sm text-slate-500">{t("goal_no_plan")}</p>
            <Button onClick={generatePlan} disabled={planning} data-testid="goal-generate-plan-btn" className="mt-3 gap-1.5 rounded-full">
              {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("goal_generate_plan")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="goal-milestones">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("goal_milestones")}</p>
            {ms.map((m, i) => (
              <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`milestone-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className={`font-medium text-slate-900 ${m.done ? "line-through text-slate-400" : ""}`}>{i + 1}. {m.title}</p>
                  <div className="flex flex-shrink-0 items-center gap-1.5">
                    {m.priority && <Badge variant="outline" className="rounded-full text-[10px] uppercase">{m.priority}</Badge>}
                    {m.target && <span className="text-xs text-slate-400">{m.target}</span>}
                  </div>
                </div>
                {m.depends_on && <p className="mt-0.5 text-xs text-slate-400">↳ needs: {m.depends_on}</p>}
                <div className="mt-2 space-y-1.5">
                  {(m.steps || []).map((s) => (
                    <button key={s.id} onClick={() => toggleStep(m.id, s.id, !s.done)} data-testid={`step-${s.id}`}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                      <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${s.done ? "border-primary bg-primary text-white" : "border-slate-300"}`}>
                        {s.done && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span className={s.done ? "text-slate-400 line-through" : "text-slate-700"}>{s.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={generatePlan} disabled={planning} variant="outline" data-testid="goal-regenerate-btn" className="gap-1.5 rounded-full">
              {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("goal_regenerate_plan")}
            </Button>
          </div>
        )}

        <div>
          <div className="flex gap-2">
            <Input value={smart} onChange={(e) => setSmart(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applySmart()}
              placeholder={t("goal_smart_ph")} data-testid="goal-smart-input" className="min-h-[44px]" maxLength={500} />
            <Button onClick={applySmart} disabled={smartBusy || smart.trim().length < 2} data-testid="goal-smart-apply" className="flex-shrink-0 gap-1.5 rounded-full">
              {smartBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-start">
          {goal.status === "paused" ? (
            <Button variant="outline" onClick={() => setStatus("active")} data-testid="goal-resume-btn" className="gap-1.5"><Play className="h-4 w-4" /> {t("goal_resume")}</Button>
          ) : (
            <Button variant="outline" onClick={() => setStatus("paused")} data-testid="goal-pause-btn" className="gap-1.5"><Pause className="h-4 w-4" /> {t("goal_pause")}</Button>
          )}
          {goal.status !== "completed" && (
            <Button variant="outline" onClick={() => setStatus("completed")} data-testid="goal-complete-btn" className="gap-1.5"><CheckCircle2 className="h-4 w-4" /> {t("goal_complete")}</Button>
          )}
          {goal.status === "completed" && (
            <Button variant="outline" onClick={() => setStatus("archived")} data-testid="goal-archive-btn" className="gap-1.5"><Archive className="h-4 w-4" /> {t("goal_archive")}</Button>
          )}
          <Button variant="ghost" onClick={remove} data-testid="goal-delete-btn" className="gap-1.5 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /> {t("goal_delete")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
